import React, { useCallback, useEffect, useMemo, useState } from "react";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import Swal from "sweetalert2";
import { confirmAlert, errorAlert, successAlert } from "../../utils/alert";
import Modal from "../../components/common/Modal";
import SearchableSelect from "../../components/common/SearchableSelect";
import MediaViewerModal from "../../components/common/MediaViewerModal";
import {
  FaUserTie,
  FaPlus,
  FaSearch,
  FaEdit,
  FaTrash,
  FaKey,
  FaBan,
  FaCheck,
  FaIdCard,
  FaFileAlt,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
} from "react-icons/fa";

const initialForm = {
  name: "",
  relation: "",
  gender: "Male",
  mobile: "",
  email: "",
  password: "",
  district: "",
  district_id: "",
  mandal: "",
  postoffice: "",
  address: "",
  pincode: "",
  state: "Andhra Pradesh",
  aadhaar_number: "",
  designation: "",
};

const AP_DISTRICTS = [
  "ALLURI SITHARAMA RAJU",
  "ANAKAPALLI",
  "ANANTHAPUR",
  "ANNAMYYA",
  "BAPATLA",
  "CHITTOOR",
  "EAST GODAVARI",
  "ELURU",
  "GUNTUR",
  "KAKINADA",
  "NTR",
  "PALNADU",
  "PARVATHIPURAM MANYAM",
  "PRAKASAM",
  "NANDYAL",
  "SRI POTTI SRIRAMULU NELLORE",
  "SRI SATHYA SAI",
  "SRIKAKULAM",
  "TIRUPATI",
  "VISAKHAPATNAM",
  "VIZIANAGARAM",
  "WEST GODAVARI",
  "YSR KADAPA",
  "KURNOOL",
  "KRISHNA",
  "DR. B.R. AMBEDKAR KONASEEMA",
];

const ManageOfficials = () => {
  const [officials, setOfficials] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedDistrictFilter, setSelectedDistrictFilter] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [existingPhoto, setExistingPhoto] = useState(null);
  const [existingAadhaarDoc, setExistingAadhaarDoc] = useState(null);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchDistrictOptions = useCallback(async () => {
    try {
      const rows = await API.fetchDistrictOptions();
      setDistrictOptions(Array.isArray(rows) ? rows : []);
    } catch {
      // fallback
    }
  }, []);

  const fetchOfficialsList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await API.fetchOfficials();
      const list = Array.isArray(data) ? data : data?.data || [];
      setOfficials(list);
    } catch (err) {
      console.error("Failed to load officials:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDistrictOptions();
    fetchOfficialsList();
  }, [fetchDistrictOptions, fetchOfficialsList]);

  const handleOpenAddModal = () => {
    setEditingId(null);
    setForm({ ...initialForm, password: "official@" + Math.floor(1000 + Math.random() * 9000) });
    setPhotoFile(null);
    setAadhaarFile(null);
    setExistingPhoto(null);
    setExistingAadhaarDoc(null);
    setIsFormModalOpen(true);
  };

  const handleOpenEditModal = (official) => {
    setEditingId(official.id);
    setForm({
      name: official.name || "",
      relation: official.relation || "",
      gender: official.gender || "Male",
      mobile: official.mobile || "",
      email: official.email || "",
      password: "",
      district: official.district || "",
      district_id: official.district_id || "",
      mandal: official.mandal || "",
      postoffice: official.postoffice || "",
      address: official.address || "",
      pincode: official.pincode || "",
      state: official.state || "Andhra Pradesh",
      aadhaar_number: official.aadhaar_number || "",
      designation: official.designation || "",
    });
    setPhotoFile(null);
    setAadhaarFile(null);
    setExistingPhoto(official.photo || null);
    setExistingAadhaarDoc(official.aadhaar || null);
    setIsFormModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsFormModalOpen(false);
    setEditingId(null);
    setForm(initialForm);
    setPhotoFile(null);
    setAadhaarFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("relation", form.relation || "");
      formData.append("gender", form.gender || "Male");
      formData.append("mobile", form.mobile || "");
      formData.append("email", form.email);
      formData.append("role", "official");
      if (form.password) {
        formData.append("password", form.password);
      }
      formData.append("district", form.district || "");
      if (form.district_id) {
        formData.append("district_id", form.district_id);
      }
      formData.append("mandal", form.mandal || "");
      formData.append("postoffice", form.postoffice || "");
      formData.append("address", form.address || "");
      formData.append("pincode", form.pincode || "");
      formData.append("state", form.state || "Andhra Pradesh");
      formData.append("aadhaar_number", form.aadhaar_number || "");
      formData.append("designation", form.designation || "");

      if (photoFile) {
        formData.append("photo", photoFile);
      }
      if (aadhaarFile) {
        formData.append("aadhaar", aadhaarFile);
      }

      if (editingId) {
        await API.updateOfficial(editingId, formData);
        successAlert("Success", "Official login updated successfully!");
      } else {
        await API.createOfficial(formData);
        successAlert("Success", "Official login created successfully!");
      }

      handleCloseModal();
      fetchOfficialsList();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to save official.";
      errorAlert("Error", msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    const confirmed = await confirmAlert({
      title: "Delete Official?",
      text: `Are you sure you want to delete login for ${name}?`,
      confirmText: "Yes, Delete",
    });
    if (!confirmed?.isConfirmed) return;

    try {
      await API.deleteOfficial(id);
      successAlert("Deleted", "Official login removed successfully.");
      fetchOfficialsList();
    } catch (err) {
      errorAlert("Error", err?.response?.data?.message || "Failed to delete official.");
    }
  };

  const handleResetPassword = async (id, name) => {
    const { value: newPassword } = await Swal.fire({
      title: `Reset Password for ${name}`,
      input: "text",
      inputLabel: "Enter New Password",
      inputValue: "official@" + Math.floor(1000 + Math.random() * 9000),
      showCancelButton: true,
      confirmButtonColor: "#0f766e",
      confirmButtonText: "Update Password",
      inputValidator: (val) => {
        if (!val || val.length < 6) return "Password must be at least 6 characters";
      },
    });

    if (!newPassword) return;

    try {
      await API.resetAdminUserPassword(id, newPassword);
      successAlert("Updated", `Password for ${name} reset to: ${newPassword}`);
    } catch (err) {
      errorAlert("Error", "Failed to reset password");
    }
  };

  const handleToggleBlock = async (official) => {
    const willBlock = !official.is_blocked;
    const confirmed = await confirmAlert({
      title: willBlock ? "Block Official?" : "Unblock Official?",
      text: willBlock
        ? "This official will be prevented from logging into the portal."
        : "Restore portal access for this official.",
      confirmText: willBlock ? "Yes, Block" : "Yes, Unblock",
    });
    if (!confirmed?.isConfirmed) return;

    try {
      await API.updateDistrictUserBlockStatus(official.id, willBlock, willBlock ? "Blocked by admin" : "");
      successAlert("Updated", willBlock ? "Official blocked." : "Official unblocked.");
      fetchOfficialsList();
    } catch (err) {
      errorAlert("Error", "Failed to update block status.");
    }
  };

  const filteredOfficials = useMemo(() => {
    return officials.filter((off) => {
      const matchesSearch =
        !search ||
        off.name?.toLowerCase().includes(search.toLowerCase()) ||
        off.email?.toLowerCase().includes(search.toLowerCase()) ||
        off.mobile?.includes(search) ||
        off.designation?.toLowerCase().includes(search.toLowerCase()) ||
        off.district?.toLowerCase().includes(search.toLowerCase());

      const matchesDistrict =
        !selectedDistrictFilter ||
        off.district?.toLowerCase() === selectedDistrictFilter.toLowerCase();

      return matchesSearch && matchesDistrict;
    });
  }, [officials, search, selectedDistrictFilter]);

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <Navbar />

      <main className="flex-1 max-w-7xl w-full mx-auto p-3 sm:p-6 lg:p-8 space-y-6">
        {/* Header Strip */}
        <div className="bg-gradient-to-br from-teal-50 via-white to-cyan-50 border border-teal-200 rounded-2xl p-4 sm:p-6 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-teal-800 bg-teal-100/80 px-2.5 py-1 rounded-full">
              Administrative Desks &bull; Role Official
            </span>
            <h1 className="text-xl sm:text-2xl font-black text-slate-900 mt-1 flex items-center gap-2">
              <FaUserTie className="text-teal-700" /> Officials Management
            </h1>
            <p className="text-xs text-slate-600 mt-1">
              Create and manage official logins with comprehensive personal, identity, address, and document credentials.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleOpenAddModal}
              className="px-4 py-2.5 bg-teal-700 hover:bg-teal-800 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm transition"
            >
              <FaPlus /> Add New Official
            </button>
          </div>
        </div>

        {/* Filter & Search Bar */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="relative">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
            <input
              type="text"
              placeholder="Search by name, mobile, email, designation..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <select
              value={selectedDistrictFilter}
              onChange={(e) => setSelectedDistrictFilter(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">-- All Districts ({officials.length}) --</option>
              {AP_DISTRICTS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between sm:justify-end gap-2 text-xs font-bold text-slate-600">
            <span>Showing: {filteredOfficials.length} Official(s)</span>
          </div>
        </div>

        {/* Officials Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-800">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-600 font-bold">
                <tr>
                  <th className="p-3.5 pl-4">Official Details</th>
                  <th className="p-3.5">Contact &amp; District</th>
                  <th className="p-3.5">Designation &amp; Relation</th>
                  <th className="p-3.5">Aadhaar &amp; Documents</th>
                  <th className="p-3.5 text-center">Status</th>
                  <th className="p-3.5 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-400 font-medium">
                      Loading officials list...
                    </td>
                  </tr>
                ) : filteredOfficials.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-10 text-center text-slate-400">
                      No officials found. Click "Add New Official" to create the first official login.
                    </td>
                  </tr>
                ) : (
                  filteredOfficials.map((off) => {
                    const avatarUrl = off.photo ? resolveAssetUrl(off.photo) : null;
                    const aadhaarDocUrl = off.aadhaar ? resolveAssetUrl(off.aadhaar) : null;

                    return (
                      <tr key={off.id} className="hover:bg-slate-50/70 transition">
                        {/* Profile & Name */}
                        <td className="p-3.5 pl-4">
                          <div className="flex items-center gap-3">
                            {avatarUrl ? (
                              <img
                                src={avatarUrl}
                                alt={off.name}
                                className="w-10 h-10 rounded-xl object-cover border border-slate-200 shadow-sm cursor-pointer"
                                onClick={() => setViewerDoc({ url: avatarUrl, title: `${off.name} - Photo` })}
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-xl bg-teal-100 text-teal-800 flex items-center justify-center font-black text-sm">
                                {off.name?.charAt(0) || "O"}
                              </div>
                            )}
                            <div>
                              <div className="font-extrabold text-slate-950 text-xs flex items-center gap-1.5">
                                {off.name}
                                <span className="px-1.5 py-0.5 rounded bg-teal-50 text-teal-800 text-[9px] font-bold border border-teal-200">
                                  {off.gender || "Official"}
                                </span>
                              </div>
                              <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                                ID: #{off.id}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Contact & District */}
                        <td className="p-3.5">
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1.5 text-slate-800 font-medium">
                              <FaPhone className="text-teal-600 text-[10px]" /> {off.mobile || "—"}
                            </div>
                            <div className="flex items-center gap-1.5 text-slate-600 text-[10px]">
                              <FaEnvelope className="text-slate-400 text-[10px]" /> {off.email}
                            </div>
                            <div className="flex items-center gap-1.5 text-teal-900 font-bold text-[10px]">
                              <FaMapMarkerAlt className="text-amber-600 text-[10px]" /> {off.district || "AP"}
                            </div>
                          </div>
                        </td>

                        {/* Designation & Relation */}
                        <td className="p-3.5">
                          <div>
                            <span className="font-bold text-slate-900 block text-xs">
                              {off.designation || "State / Technical Official"}
                            </span>
                            {off.relation && (
                              <span className="text-[10px] text-slate-500 block">
                                S/o, D/o, W/o: {off.relation}
                              </span>
                            )}
                            {off.mandal && (
                              <span className="text-[10px] text-slate-500 block">
                                Mandal: {off.mandal} {off.pincode ? `(${off.pincode})` : ""}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Aadhaar & Docs */}
                        <td className="p-3.5">
                          <div className="space-y-1">
                            <div className="flex items-center gap-1.5 font-mono text-[11px] font-bold text-slate-800">
                              <FaIdCard className="text-cyan-700" />
                              {off.aadhaar_number ? `XXXX-XXXX-${off.aadhaar_number.slice(-4)}` : "—"}
                            </div>
                            {aadhaarDocUrl ? (
                              <button
                                type="button"
                                onClick={() => setViewerDoc({ url: aadhaarDocUrl, title: `${off.name} - Aadhaar Doc` })}
                                className="inline-flex items-center gap-1 text-[10px] text-teal-700 hover:underline font-bold"
                              >
                                <FaFileAlt /> View Document
                              </button>
                            ) : (
                              <span className="text-[10px] text-slate-400">No doc uploaded</span>
                            )}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="p-3.5 text-center">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold border ${
                              off.is_blocked
                                ? "bg-rose-50 text-rose-700 border-rose-200"
                                : "bg-emerald-50 text-emerald-700 border-emerald-200"
                            }`}
                          >
                            {off.is_blocked ? "BLOCKED" : "ACTIVE"}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="p-3.5 pr-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => handleOpenEditModal(off)}
                              title="Edit Official"
                              className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => handleResetPassword(off.id, off.name)}
                              title="Reset Password"
                              className="p-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 transition"
                            >
                              <FaKey />
                            </button>
                            <button
                              onClick={() => handleToggleBlock(off)}
                              title={off.is_blocked ? "Unblock Official" : "Block Official"}
                              className={`p-1.5 rounded-lg border transition ${
                                off.is_blocked
                                  ? "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200"
                                  : "bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-200"
                              }`}
                            >
                              {off.is_blocked ? <FaCheck /> : <FaBan />}
                            </button>
                            <button
                              onClick={() => handleDelete(off.id, off.name)}
                              title="Delete Official"
                              className="p-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 transition"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* CREATE / EDIT MODAL (Full details same as player except sadarem, dob) */}
      <Modal
        isOpen={isFormModalOpen}
        onClose={handleCloseModal}
        title={editingId ? "Edit Official Profile & Login" : "Create New Official Profile & Login"}
        width="820px"
      >
        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 text-teal-900">
            <span className="font-bold">Role: Official</span> &bull; Full player credentials (personal, address, contact, and identity docs) configured without SADAREM and DOB.
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {/* Full Name */}
            <div>
              <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                Full Name <span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                required
                placeholder="e.g. Ramesh Kumar"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* Father / Guardian / Spouse Relation Name */}
            <div>
              <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                Relation / Father / Spouse Name
              </label>
              <input
                type="text"
                placeholder="e.g. S/o or W/o Name"
                value={form.relation}
                onChange={(e) => setForm({ ...form, relation: e.target.value })}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* Gender */}
            <div>
              <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                Gender <span className="text-rose-600">*</span>
              </label>
              <select
                required
                value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-teal-500"
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Designation / Department */}
            <div>
              <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                Official Designation / Title
              </label>
              <input
                type="text"
                placeholder="e.g. Chief Referee / Technical Official"
                value={form.designation}
                onChange={(e) => setForm({ ...form, designation: e.target.value })}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* Mobile */}
            <div>
              <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                Mobile Number <span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                required
                maxLength={15}
                placeholder="10-digit mobile"
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "") })}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* Email (Login ID) */}
            <div>
              <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                Email (Login Username) <span className="text-rose-600">*</span>
              </label>
              <input
                type="email"
                required
                placeholder="official@parasports.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                Password {editingId ? "(Leave blank to keep current)" : <span className="text-rose-600">*</span>}
              </label>
              <input
                type="text"
                required={!editingId}
                placeholder={editingId ? "Unchanged" : "Min 6 characters"}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* District */}
            <div>
              <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                District <span className="text-rose-600">*</span>
              </label>
              <SearchableSelect
                name="district"
                value={form.district}
                onChange={(e) => {
                  const dName = e.target.value;
                  const matched = districtOptions.find(
                    (x) => x.name?.toLowerCase() === dName?.toLowerCase()
                  );
                  setForm({
                    ...form,
                    district: dName,
                    district_id: matched ? matched.id : "",
                  });
                }}
                required
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Select District</option>
                {AP_DISTRICTS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </SearchableSelect>
            </div>

            {/* Mandal */}
            <div>
              <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                Mandal
              </label>
              <input
                type="text"
                placeholder="Mandal name"
                value={form.mandal}
                onChange={(e) => setForm({ ...form, mandal: e.target.value })}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* Post Office / Village */}
            <div>
              <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                Post Office / Village
              </label>
              <input
                type="text"
                placeholder="Post Office name"
                value={form.postoffice}
                onChange={(e) => setForm({ ...form, postoffice: e.target.value })}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* Pincode */}
            <div>
              <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                Pincode
              </label>
              <input
                type="text"
                maxLength={6}
                placeholder="6-digit pincode"
                value={form.pincode}
                onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, "") })}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* Aadhaar Number */}
            <div>
              <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                Aadhaar Number (12 Digits)
              </label>
              <input
                type="text"
                maxLength={12}
                placeholder="12-digit Aadhaar"
                value={form.aadhaar_number}
                onChange={(e) => setForm({ ...form, aadhaar_number: e.target.value.replace(/\D/g, "") })}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* Address */}
            <div className="sm:col-span-2">
              <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                Residential / Postal Address
              </label>
              <textarea
                rows={2}
                placeholder="Door No, Street, Landmark..."
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-slate-900 font-semibold focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* Photo Upload */}
            <div className="border border-slate-200 rounded-xl p-3 bg-slate-50">
              <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                Official Photo (JPG/PNG)
              </label>
              {existingPhoto && !photoFile && (
                <div className="mb-2 flex items-center gap-2">
                  <img
                    src={resolveAssetUrl(existingPhoto)}
                    alt="Current"
                    className="w-12 h-12 rounded-lg object-cover border"
                  />
                  <span className="text-[10px] text-slate-500">Current Photo on file</span>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setPhotoFile(e.target.files[0] || null)}
                className="w-full text-xs text-slate-600 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-teal-700 file:text-white file:font-semibold"
              />
            </div>

            {/* Aadhaar Document Upload */}
            <div className="border border-slate-200 rounded-xl p-3 bg-slate-50">
              <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                Aadhaar Document (PDF/JPG)
              </label>
              {existingAadhaarDoc && !aadhaarFile && (
                <div className="mb-2">
                  <button
                    type="button"
                    onClick={() => setViewerDoc({ url: resolveAssetUrl(existingAadhaarDoc), title: "Aadhaar Document" })}
                    className="text-teal-700 hover:underline font-bold text-[11px] flex items-center gap-1"
                  >
                    <FaFileAlt /> View Uploaded Aadhaar
                  </button>
                </div>
              )}
              <input
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => setAadhaarFile(e.target.files[0] || null)}
                className="w-full text-xs text-slate-600 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-teal-700 file:text-white file:font-semibold"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200">
            <button
              type="button"
              onClick={handleCloseModal}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold transition text-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-bold shadow-sm transition text-xs disabled:opacity-50"
            >
              {saving ? "Saving..." : editingId ? "Update Official" : "Create Official Login"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Media Viewer Modal */}
      {viewerDoc && (
        <MediaViewerModal
          isOpen={Boolean(viewerDoc)}
          onClose={() => setViewerDoc(null)}
          mediaUrl={viewerDoc.url}
          title={viewerDoc.title}
        />
      )}
    </div>
  );
};

export default ManageOfficials;
